import { Component } from '@theme/component';
import { fetchConfig, preloadImage, onAnimationEnd, yieldToMainThread } from '@theme/utilities';
import { ThemeEvents, CartAddEvent, CartErrorEvent, CartUpdateEvent, VariantUpdateEvent } from '@theme/events';
import { cartPerformance } from '@theme/performance';
import { morph } from '@theme/morph';

// Error message display duration - gives users time to read the message
const ERROR_MESSAGE_DISPLAY_DURATION = 10000;

// Button re-enable delay after error - prevents rapid repeat attempts
const ERROR_BUTTON_REENABLE_DELAY = 1000;

// Success message display duration for screen readers
const SUCCESS_MESSAGE_DISPLAY_DURATION = 5000;

/**
 * Module-level reference to the customer's current cart.
 *
 * Kept fresh in the background (primed on connect, refreshed after every cart
 * mutation) so the footwear size-map dedupe check at add-to-cart time is
 * synchronous — no `/cart.js` round trip before the add, which keeps feedback
 * as fast as a normal add.
 *
 * @type {{items?: Array<{variant_id: number, key: string, quantity: number, properties: Record<string, string> | null}>} | null}
 */
let cachedCart = null;

/** @type {Promise<typeof cachedCart> | null} */
let cartRefreshInFlight = null;

/**
 * @returns {typeof cachedCart}
 */
function getCachedCart() {
  return cachedCart;
}

/**
 * Refreshes the cached cart from `/cart.js`. Concurrent calls share a single
 * request; the previous cache is left intact if the fetch fails.
 * @returns {Promise<typeof cachedCart>}
 */
function refreshCachedCart() {
  if (cartRefreshInFlight) return cartRefreshInFlight;

  cartRefreshInFlight = (async () => {
    try {
      const response = await fetch('/cart.js');
      cachedCart = await response.json();
    } catch (error) {
      console.error('Failed to refresh cached cart:', error);
    } finally {
      cartRefreshInFlight = null;
    }
    return cachedCart;
  })();

  return cartRefreshInFlight;
}

/**
 * @typedef {HTMLElement & {
 *   source: Element,
 *   destination: Element,
 *   useSourceSize: string | boolean
 * }} FlyToCart
 */

/**
 * A custom element that manages an add to cart button.
 *
 * @typedef {object} AddToCartRefs
 * @property {HTMLButtonElement} addToCartButton - The add to cart button.
 * @extends Component<AddToCartRefs>
 */
export class AddToCartComponent extends Component {
  requiredRefs = [];

  /** @type {number[] | undefined} */
  #resetTimeouts = /** @type {number[]} */ ([]);

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('pointerenter', this.#preloadImage);
    this.#setupSizeGuideTrigger();
  }

  /**
   * Sets up the event listener for the size guide trigger button.
   * Called on connect and after morphing when variant changes.
   */
  #setupSizeGuideTrigger() {
    const existingTrigger = this.querySelector('[data-size-guide-trigger="true"]');
    if (existingTrigger) {
      existingTrigger.removeEventListener('click', this.#handleSizeGuideClick);
    }
    
    const sizeGuideTrigger = this.querySelector('[data-size-guide-trigger="true"]');
    if (sizeGuideTrigger) {
      sizeGuideTrigger.addEventListener('click', this.#handleSizeGuideClick);
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    if (this.#resetTimeouts) {
      this.#resetTimeouts.forEach(/** @param {number} timeoutId */ (timeoutId) => clearTimeout(timeoutId));
    }
    this.removeEventListener('pointerenter', this.#preloadImage);
    
    const sizeGuideTrigger = this.querySelector('[data-size-guide-trigger="true"]');
    if (sizeGuideTrigger) {
      sizeGuideTrigger.removeEventListener('click', this.#handleSizeGuideClick);
    }
  }

  /**
   * Disables the add to cart button.
   */
  disable() {
    if (this.refs.addToCartButton) {
      this.refs.addToCartButton.disabled = true;
      this.refs.addToCartButton.setAttribute('aria-disabled', 'true');
    }
  }

  /**
   * Enables the add to cart button.
   */
  enable() {
    if (this.refs.addToCartButton) {
      this.refs.addToCartButton.disabled = false;
      this.refs.addToCartButton.removeAttribute('aria-disabled');
    }
  }

  /**
   * Handles clicks on the size guide trigger button - opens size guide modal.
   * @param {MouseEvent & {target: HTMLElement}} event - The click event.
   */
  #handleSizeGuideClick = (event) => {
    event.preventDefault();
    event.stopPropagation();
    
    // Open size guide modal - the .size-guide-modal element is the dialog-component itself
    // This matches the pattern used in click-collect-drawer.liquid
    const sizeGuideModal = document.querySelector('.size-guide-modal');
    if (sizeGuideModal && typeof sizeGuideModal.showDialog === 'function') {
      sizeGuideModal.showDialog();
    }
  };

  /**
   * Handles the click event for the add to cart button.
   * @param {MouseEvent & {target: HTMLElement}} event - The click event.
   */
  handleClick(event) {
    const productForm = /** @type {ProductFormComponent | null} */ (this.closest('product-form-component'));

    // If current variant is not available, use Add to Cart as a "Notify me" trigger instead of submitting the form
    if (productForm?.dataset.variantAvailable === 'false') {
      event.preventDefault();
      event.stopPropagation();

      const notifyDrawer = /** @type {HTMLElement & { showDialog?: () => void } | null} */ (
        productForm.querySelector('.notify-me-drawer')
      );

      if (notifyDrawer?.showDialog) {
        notifyDrawer.showDialog();
      }

      return;
    }

    const form = this.closest('form');
    if (!form?.checkValidity()) return;

    // Check if adding would exceed max before animating
    const quantitySelector = productForm?.refs.quantitySelector;
    if (quantitySelector?.canAddToCart) {
      const validation = quantitySelector.canAddToCart();
      // Don't animate if it would exceed max
      if (!validation.canAdd) {
        return;
      }
    }
    if (this.refs.addToCartButton && this.refs.addToCartButton.dataset.puppet !== 'true') {
      const animationEnabled = this.dataset.addToCartAnimation === 'true';
      if (animationEnabled && !event.target.closest('.quick-add-modal')) {
        this.#animateFlyToCart();
      }
      this.animateAddToCart();
    }
  }

  #preloadImage = () => {
    const image = this.dataset.productVariantMedia;

    if (!image) return;

    preloadImage(image);
  };

  /**
   * Animates the fly to cart animation.
   */
  #animateFlyToCart() {
    const { addToCartButton } = this.refs;
    const cartIcon = document.querySelector('.header-actions__cart-icon');

    const image = this.dataset.productVariantMedia;

    if (!cartIcon || !addToCartButton || !image) return;

    const flyToCartElement = /** @type {FlyToCart} */ (document.createElement('fly-to-cart'));

    let flyToCartClass = addToCartButton.classList.contains('quick-add__button')
      ? 'fly-to-cart--quick'
      : 'fly-to-cart--main';

    flyToCartElement.classList.add(flyToCartClass);
    flyToCartElement.style.setProperty('background-image', `url(${image})`);
    flyToCartElement.style.setProperty('--start-opacity', '0');
    flyToCartElement.source = addToCartButton;
    flyToCartElement.destination = cartIcon;

    document.body.appendChild(flyToCartElement);
  }

  /**
   * Animates the add to cart button.
   */
  animateAddToCart = async function () {
    const { addToCartButton } = this.refs;

    // Initialize the array if it doesn't exist
    if (!this.#resetTimeouts) {
      this.#resetTimeouts = [];
    }

    // Clear all existing timeouts
    this.#resetTimeouts.forEach(/** @param {number} timeoutId */ (timeoutId) => clearTimeout(timeoutId));
    this.#resetTimeouts = [];

    if (addToCartButton.dataset.added !== 'true') {
      addToCartButton.dataset.added = 'true';
    }

    // The onAnimationEnd can trigger a style recalculation so we yield to the main thread first.
    await yieldToMainThread();
    await onAnimationEnd(addToCartButton);

    // Create new timeout and store it in the array
    const timeoutId = setTimeout(() => {
      addToCartButton.removeAttribute('data-added');

      // Remove this timeout from the array
      const index = this.#resetTimeouts.indexOf(timeoutId);
      if (index > -1) {
        this.#resetTimeouts.splice(index, 1);
      }
    }, 800);

    this.#resetTimeouts.push(timeoutId);
  };
}

if (!customElements.get('add-to-cart-component')) {
  customElements.define('add-to-cart-component', AddToCartComponent);
}

/**
 * A custom element that manages a product form.
 *
 * @typedef {{items: Array<{quantity: number, variant_id: number, product_id: number}>}} Cart
 *
 * @typedef {object} ProductFormRefs
 * @property {HTMLInputElement} variantId - The form input for submitting the variant ID.
 * @property {AddToCartComponent | undefined} addToCartButtonContainer - The add to cart button container element.
 * @property {HTMLElement | undefined} addToCartTextError - The add to cart text error.
 * @property {HTMLElement | undefined} acceleratedCheckoutButtonContainer - The accelerated checkout button container element.
 * @property {HTMLElement} liveRegion - The live region.
 * @property {HTMLElement | undefined} quantityLabelCartCount - The quantity label cart count element.
 * @property {HTMLElement | undefined} quantityRules - The quantity rules element.
 * @property {HTMLElement | undefined} productFormButtons - The product form buttons container.
 * @property {HTMLElement | undefined} volumePricing - The volume pricing component.
 * @property {any | undefined} quantitySelector - The quantity selector component.
 * @property {HTMLElement | undefined} quantitySelectorWrapper - The quantity selector wrapper element.
 * @property {HTMLElement | undefined} quantityLabel - The quantity label element.
 * @property {HTMLElement | undefined} pricePerItem - The price per item component.
 *
 * @extends Component<ProductFormRefs>
 */
class ProductFormComponent extends Component {
  requiredRefs = ['variantId', 'liveRegion'];
  #abortController = new AbortController();

  /** @type {number | undefined} */
  #timeout;

  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#abortController;
    const target = this.closest('.shopify-section, dialog, product-card');
    target?.addEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate, { signal });
    target?.addEventListener(ThemeEvents.variantSelected, this.#onVariantSelected, { signal });

    // Listen for cart updates to sync data-cart-quantity
    document.addEventListener(ThemeEvents.cartUpdate, this.#onCartUpdate, { signal });

    // Prime the cached cart reference so the size-map dedupe check is instant.
    void refreshCachedCart();

    if (this.dataset.isHighHeat === 'true') {
      void this.#fetchAndUpdateCartQuantity();
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#abortController.abort();
  }

  /**
   * Updates quantity selector with cart data for current variant
   * @param {Cart} cart - The cart object with items array
   * @returns {number} The cart quantity for the current variant
   */
  #updateCartQuantityFromData(cart) {
    const variantIdInput = /** @type {HTMLInputElement | null} */ (this.querySelector('input[name="id"]'));
    if (!variantIdInput?.value || !cart?.items) return 0;

    const cartItem = cart.items.find((item) => item.variant_id.toString() === variantIdInput.value.toString());
    const cartQty = cartItem ? cartItem.quantity : 0;

    // Use public API to update quantity selector
    const quantitySelector = /** @type {any | undefined} */ (this.querySelector('quantity-selector-component'));
    if (quantitySelector?.setCartQuantity) {
      quantitySelector.setCartQuantity(cartQty);
    }

    // Update quantity label if it exists
    this.#updateQuantityLabel(cartQty);

    this.#applyHighHeatButtonState(cart);

    return cartQty;
  }

  /**
   * @param {HTMLElement} source
   */
  #getHighHeatMaxButtonLabel(source) {
    const raw = source?.dataset?.highHeatMaxButtonLabel?.trim();
    if (raw) return raw;
    const limit = Math.max(1, parseInt(String(source?.dataset?.highHeatDemandLimit ?? '1'), 10) || 1);
    return `Maximum of ${limit} Product`;
  }

  /**
   * Sets or restores the visible label for an add-to-cart button.
   * Preserves the price element when present.
   * @param {HTMLButtonElement} button
   * @param {boolean} inCart
   */
  #setHighHeatButtonText(button, inCart) {
    const labelContainer = /** @type {HTMLElement | null} */ (button.querySelector('.add-to-cart-text__content span span'));
    if (!labelContainer) return;

    if (!labelContainer.dataset.defaultHtml) {
      labelContainer.dataset.defaultHtml = labelContainer.dataset.defaultLabelHtml || labelContainer.innerHTML;
    }

    const price = /** @type {HTMLElement | null} */ (labelContainer.querySelector('.add-to-cart-text__price'));

    if (inCart) {
      if (price) price.setAttribute('hidden', 'true');
      const priceClone = price ? price.cloneNode(true) : null;
      const labelText = this.#getHighHeatMaxButtonLabel(this);
      labelContainer.textContent = labelText;
      if (priceClone) {
        labelContainer.appendChild(document.createTextNode(' '));
        labelContainer.appendChild(/** @type {Node} */ (priceClone));
      }
    } else {
      if (price) price.removeAttribute('hidden');
      labelContainer.innerHTML = labelContainer.dataset.defaultHtml || labelContainer.innerHTML;
    }
  }

  /**
   * Sets or restores the sticky bar button label.
   * @param {HTMLElement} stickyRoot
   * @param {boolean} inCart
   */
  #setHighHeatStickyText(stickyRoot, inCart) {
    const stickyBtn = /** @type {HTMLButtonElement | null} */ (stickyRoot.querySelector('[ref="addToCartButton"]'));
    if (!stickyBtn) return;

    const label = /** @type {HTMLElement | null} */ (stickyBtn.querySelector('.add-to-cart-text__content > span'));
    if (!label) return;

    if (!label.dataset.defaultText) {
      label.dataset.defaultText = label.getAttribute('data-default-text') || label.textContent?.trim() || '';
    }

    const labelText = this.#getHighHeatMaxButtonLabel(stickyRoot);

    label.textContent = inCart ? labelText : (label.dataset.defaultText || label.textContent || '');
  }

  /**
   * Disables add-to-cart (and sticky bar / accelerated checkout) when a high-heat product is already in the cart.
   * @param {Cart | undefined} cart
   */
  #applyHighHeatButtonState(cart) {
    if (this.dataset.isHighHeat !== 'true') return;

    const productId = Number(this.dataset.productId);
    const items = cart?.items;
    if (!items) {
      void this.#fetchAndUpdateCartQuantity();
      return;
    }

    // Empty cart must clear the high-heat lock.
    if (items.length === 0) {
      this.dataset.highHeatInCart = 'false';
    }

    const hasProductId = typeof items[0]?.product_id === 'number';
    if (!hasProductId && items.length > 0) {
      void this.#fetchAndUpdateCartQuantity();
      return;
    }

    const limit = Math.max(1, parseInt(String(this.dataset.highHeatDemandLimit ?? '1'), 10) || 1);
    let productQty = 0;
    for (const item of items) {
      if (item.product_id === productId) productQty += item.quantity;
    }
    const inCart = items.length > 0 && productQty >= limit;

    this.dataset.highHeatInCart = inCart ? 'true' : 'false';

    const allAddToCartContainers = /** @type {NodeListOf<AddToCartComponent>} */ (
      this.querySelectorAll('add-to-cart-component')
    );
    const variantAvailable = this.dataset.variantAvailable === 'true';

    for (const container of allAddToCartContainers) {
      const btn = container.refs.addToCartButton;
      if (btn) this.#setHighHeatButtonText(btn, inCart);

      if (inCart) {
        container.disable();
      } else if (variantAvailable) {
        container.enable();
      }
    }

    const section = this.closest('.shopify-section');
    const sticky = section?.querySelector(`sticky-add-to-cart[data-product-id="${this.dataset.productId}"]`);
    const stickyButton = /** @type {HTMLButtonElement | null} */ (sticky?.querySelector('[ref="addToCartButton"]'));
    if (stickyButton) {
      stickyButton.disabled = inCart;
    }
    if (sticky instanceof HTMLElement) {
      this.#setHighHeatStickyText(sticky, inCart);
    }

    const accelerated = this.querySelector('[ref="acceleratedCheckoutButtonContainer"]');
    if (accelerated instanceof HTMLElement) {
      accelerated.toggleAttribute('hidden', inCart || !variantAvailable);
    }
  }

  /**
   * Fetches cart and updates quantity selector for current variant
   * @returns {Promise<number>} The cart quantity for the current variant
   */
  async #fetchAndUpdateCartQuantity() {
    const variantIdInput = /** @type {HTMLInputElement | null} */ (this.querySelector('input[name="id"]'));
    if (!variantIdInput?.value) return 0;

    // Reuse the shared cart fetch so this also refreshes the cached cart used by
    // the size-map dedupe check.
    const cart = await refreshCachedCart();
    if (!cart) return 0;

    return this.#updateCartQuantityFromData(cart);
  }

  /**
   * Updates data-cart-quantity when cart is updated from elsewhere
   * @param {CartUpdateEvent|CartAddEvent} event
   */
  #onCartUpdate = async (event) => {
    // Skip if this event came from this component
    if (event.detail?.sourceId === this.id || event.detail?.data?.source === 'product-form-component') return;

    // Keep the cached cart in sync with mutations from other surfaces (mini cart
    // quantity/remove, other tabs) so the size-map dedupe check stays accurate.
    void refreshCachedCart();

    // For high-heat products, always refetch the canonical cart so add/remove from
    // mini cart or other surfaces reliably toggles the lock + text.
    if (this.dataset.isHighHeat === 'true') {
      await this.#fetchAndUpdateCartQuantity();
      return;
    }

    const cart = /** @type {Cart} */ (event.detail?.resource);
    if (cart?.items) {
      this.#updateCartQuantityFromData(cart);
    } else {
      await this.#fetchAndUpdateCartQuantity();
    }
  };

  /**
   * Handles the submit event for the product form.
   *
   * @param {Event} event - The submit event.
   */
  handleSubmit(event) {
    const { addToCartTextError } = this.refs;
    // Stop default behaviour from the browser
    event.preventDefault();

    if (this.dataset.variantAvailable === 'false') {
      const notifyDrawer = /** @type {HTMLElement & { showDialog?: () => void } | null} */ (
        this.querySelector('.notify-me-drawer')
      );
      if (notifyDrawer?.showDialog) {
        notifyDrawer.showDialog();
      }
      return;
    }

    if (this.#timeout) clearTimeout(this.#timeout);

    // Query for ALL add-to-cart components
    const allAddToCartContainers = /** @type {NodeListOf<AddToCartComponent>} */ (
      this.querySelectorAll('add-to-cart-component')
    );

    // Check if ANY add to cart button is disabled and do an early return if it is
    const anyButtonDisabled = Array.from(allAddToCartContainers).some(
      (container) => container.refs.addToCartButton?.disabled
    );
    if (anyButtonDisabled) return;

    // Send the add to cart information to the cart
    const form = this.querySelector('form');

    if (!form) throw new Error('Product form element missing');
    console.log(this.refs.quantitySelector?.canAddToCart, ' oh')

    // A size change is still loading: the id input holds the previous variant
    // while `_selected_size` already reflects the new size (JSAU-1491).
    if (this.#isVariantIdStale()) return;

    if (this.refs.quantitySelector?.canAddToCart) {
      const validation = this.refs.quantitySelector.canAddToCart();
        console.log(validation, 'vldiation jkkk ')
      if (!validation.canAdd) {
        // Disable ALL add-to-cart buttons
        for (const container of allAddToCartContainers) {
          container.disable();
        }

        const errorTemplate = this.dataset.quantityErrorMax || '';
        const errorMessage = errorTemplate.replace('{{ maximum }}', validation.maxQuantity?.toString() || '');
        window.mrHelpers?.gtmPopupErrorMsgEvent(`Add to Cart quantity error: ${errorMessage}`);
        if (addToCartTextError) {
          addToCartTextError.classList.remove('hidden');

          const textNode = addToCartTextError.childNodes[2];
          if (textNode) {
            textNode.textContent = errorMessage;
          } else {
            const newTextNode = document.createTextNode(errorMessage);
            addToCartTextError.appendChild(newTextNode);
          }

          this.#setLiveRegionText(errorMessage);

          if (this.#timeout) clearTimeout(this.#timeout);
          this.#timeout = setTimeout(() => {
            if (!addToCartTextError) return;
            addToCartTextError.classList.add('hidden');
            this.#clearLiveRegionText();
          }, ERROR_MESSAGE_DISPLAY_DURATION);
        }

        setTimeout(() => {
          // Re-enable ALL add-to-cart buttons
          for (const container of allAddToCartContainers) {
            container.enable();
          }
        }, ERROR_BUTTON_REENABLE_DELAY);

        return;
      }
    }

    // Keep footwear size-map line item property in sync right before submit
    if (typeof window.syncSizeMapLineItemProperty === 'function') {
      window.syncSizeMapLineItemProperty();
    }

    const formData = new FormData(form);

    // Performance optimization:
    // If the mini cart drawer is configured to auto-open, requesting `sections` makes
    // the `cart/add.js` response slower because the server has to render section HTML.
    //
    // We intentionally omit `sections` in that case:
    // - Drawer can open immediately after a successful cart add response.
    // - `cart-items-component` will render the drawer section afterwards via `sectionRenderer`.
    const sectionsParam = this.#getCartSectionsToRender();
    if (sectionsParam) {
      formData.append('sections', sectionsParam);
    }

    // Footwear size-map dedupe:
    // Adding the same variant again with a different `_selected_size` line item
    // property would create a duplicate cart line (Shopify treats differing
    // properties as distinct lines). If the variant is already in the cart we
    // update that line's `_selected_size` + quantity via `cart/change.js` instead.
    //
    // The lookup is synchronous against the background-synced cached cart, so
    // there is no extra round trip before the request — feedback stays as fast
    // as a normal add.
    const selectedSize = formData.get('properties[_selected_size]');
    const existingLine = selectedSize ? this.#findCachedCartLine(formData.get('id')) : null;

    if (selectedSize && existingLine) {
      void this.#updateSizeMappedLine(existingLine, formData, String(selectedSize), event);
      return;
    }

    void this.#addToCart(formData, event);
  }

  /**
   * Resolves the comma-separated cart section IDs to re-render on add/change.
   *
   * By default returns an empty string when an auto-open drawer is present,
   * matching the `cart/add.js` performance path that omits `sections` and lets
   * the drawer render itself via `sectionRenderer` afterwards.
   *
   * Pass `forceSections` for the `cart/change.js` dedupe path so the updated
   * line HTML comes back in the response and can be morphed the instant the
   * drawer opens, rather than after a separate `sectionRenderer` round trip.
   *
   * @param {boolean} [forceSections=false] - Include sections even when a drawer auto-opens.
   * @returns {string}
   */
  #getCartSectionsToRender(forceSections = false) {
    if (!forceSections) {
      const autoOpenCartItemsComponents = document.querySelectorAll('cart-drawer-component[auto-open] cart-items-component');
      if (autoOpenCartItemsComponents.length > 0) return '';
    }

    const cartItemsComponents = document.querySelectorAll('cart-items-component');

    /** @type {string[]} */
    const cartItemComponentsSectionIds = [];
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        cartItemComponentsSectionIds.push(item.dataset.sectionId);
      }
    });

    return Array.from(new Set(cartItemComponentsSectionIds)).join(',');
  }

  /**
   * Finds the cached cart line matching a variant id.
   * @param {FormDataEntryValue | null} variantId
   * @returns {{key: string, quantity: number, properties: Record<string, string> | null} | null}
   */
  #findCachedCartLine(variantId) {
    if (!variantId) return null;

    const cart = getCachedCart();
    const line = cart?.items?.find((item) => String(item.variant_id) === String(variantId));
    return line || null;
  }

  /**
   * Default add-to-cart request against `cart/add.js`.
   * @param {FormData} formData - The submitted form data.
   * @param {Event} event - The originating submit event (for performance marks).
   * @returns {Promise<void>}
   */
  #addToCart(formData, event) {
    const fetchCfg = fetchConfig('javascript', { body: formData });

    return fetch(Theme.routes.cart_add_url, {
      ...fetchCfg,
      headers: {
        ...fetchCfg.headers,
        Accept: 'text/html',
      },
    })
      .then((response) => response.json())
      .then((response) => {
        this.#onCartResponse(
          response,
          formData.get('id'),
          Number(formData.get('quantity')) || Number(this.dataset.quantityDefault)
        );
      })
      .catch((error) => {
        console.error(error);
        window.mrHelpers?.gtmPopupErrorMsgEvent(`Add to Cart error: ${error?.message || error}`);
      })
      .finally(() => {
        cartPerformance.measureFromEvent('add:user-action', event);
      });
  }

  /**
   * Updates an existing cart line's `_selected_size` property and quantity via
   * `cart/change.js`, preserving any other line item properties.
   *
   * If the change is rejected with a 422 (the variant is already at its maximum
   * quantity in the cart), the optimistic cache is rolled back so no action is
   * taken and a max-quantity error message is shown to the customer.
   * @param {{key: string, quantity: number, properties: Record<string, string> | null}} line - The matched cart line.
   * @param {FormData} formData - The submitted form data.
   * @param {string} selectedSize - The current `_selected_size` value.
   * @param {Event} event - The originating submit event.
   * @returns {Promise<void>}
   */
  #updateSizeMappedLine(line, formData, selectedSize, event) {
    const requestedQty = Number(formData.get('quantity')) || Number(this.dataset.quantityDefault) || 1;
    const previousQuantity = Number(line.quantity) || 0;
    const newQuantity = previousQuantity + requestedQty;

    // `cart/change.js` replaces the line's properties, so merge onto the
    // existing set and only override `_selected_size`.
    /** @type {Record<string, string>} */
    const properties = { ...(line.properties || {}), _selected_size: selectedSize };

    // Capture the pre-change cache so we can roll it back if the change is rejected.
    const originalProperties = line.properties;

    // Optimistically update the cached line so a rapid re-add computes the right
    // quantity before the background refresh completes.
    line.quantity = newQuantity;
    line.properties = properties;

    // NOTE: #postCartChange already parses the JSON body, so `response` here is
    // the parsed cart (on success) or the parsed error body carrying `status:
    // 422` when the requested quantity exceeds availability. Do NOT call
    // response.json() again — that throws and masks the real 422.
    return this.#postCartChange(line.key, newQuantity, properties)
      .then((response) => {
        if (response.status) {
          // 422: the variant is already at its maximum quantity in the cart.
          // Take no action — roll back the optimistic cache — and surface the
          // max-quantity message through the shared error handler.
          line.quantity = previousQuantity;
          line.properties = originalProperties;

          this.#onCartResponse(
            { ...response, message: 'The maximum quantity of this item is already in your bag' },
            formData.get('id'),
            requestedQty
          );
          return;
        }

        // Success: the `_selected_size` label + quantity change applied.
        this.#onCartResponse(response, formData.get('id'), requestedQty);
      })
      .catch((error) => {
        console.error(error);
        window.mrHelpers?.gtmPopupErrorMsgEvent(`Add to Cart error: ${error?.message || error}`);
      })
      .finally(() => {
        cartPerformance.measureFromEvent('add:user-action', event);
      });
  }

  /**
   * Posts a single-line `cart/change.js` request, always requesting the cart
   * sections so the drawer can render the updated line immediately.
   * @param {string} key - The cart line key.
   * @param {number} quantity - The target quantity for the line.
   * @param {Record<string, string>} properties - The line item properties to set.
   * @returns {Promise<any>} The parsed JSON response.
   */
  #postCartChange(key, quantity, properties) {
    /** @type {Record<string, unknown>} */
    const body = { id: key, quantity, properties };

    // Always request sections so the drawer renders the updated line the moment
    // it opens instead of after a second round trip (see #getCartSectionsToRender).
    const sections = this.#getCartSectionsToRender(true);
    if (sections) {
      body.sections = sections;
    }

    return fetch(Theme.routes.cart_change_url, fetchConfig('json', { body: JSON.stringify(body) })).then(
      async (response) => {
        const data = await response.json();

        // Surface the HTTP status (e.g. 422) on the parsed body so callers can
        // detect a rejected change even when Shopify omits `status` from the
        // JSON body. `#onCartResponse` and `#updateSizeMappedLine` branch on it.
        if (!response.ok && data && data.status == null) {
          data.status = response.status;
        }

        return data;
      }
    );
  }

  /**
   * Shared handler for `cart/add.js` and `cart/change.js` success/error
   * responses so both request paths surface errors and dispatch the same events.
   * @param {any} response - Parsed JSON response.
   * @param {FormDataEntryValue | null} id - The variant ID that was added.
   * @param {number} itemCount - How many units were added in this action.
   */
  #onCartResponse(response, id, itemCount) {
    const { addToCartTextError } = this.refs;
    const form = this.querySelector('form');

    if (response.status) {
      this.dispatchEvent(
        new CartErrorEvent(form?.getAttribute('id') || '', response.message, response.description, response.errors)
      );
      window.mrHelpers?.gtmPopupErrorMsgEvent(`Add to Cart error: ${response.message || response.description || response.status}`);

      if (!addToCartTextError) return;
      addToCartTextError.classList.remove('hidden');

      // Reuse the text node if the user is spam-clicking
      const textNode = addToCartTextError.childNodes[2];
      if (textNode) {
        textNode.textContent = response.message;
      } else {
        const newTextNode = document.createTextNode(response.message);
        addToCartTextError.appendChild(newTextNode);
      }

      // Create or get existing error live region for screen readers
      this.#setLiveRegionText(response.message);

      this.#timeout = setTimeout(() => {
        if (!addToCartTextError) return;
        addToCartTextError.classList.add('hidden');

        // Clear the announcement
        this.#clearLiveRegionText();
      }, ERROR_MESSAGE_DISPLAY_DURATION);

      // When we add more than the maximum amount of items to the cart, we need to dispatch a cart update event
      // because our back-end still adds the max allowed amount to the cart.
      this.dispatchEvent(
        new CartAddEvent({}, this.id, {
          didError: true,
          source: 'product-form-component',
          itemCount,
          productId: this.dataset.productId,
        })
      );

      return;
    }

    if (addToCartTextError) {
      addToCartTextError.classList.add('hidden');
      addToCartTextError.removeAttribute('aria-live');
    }

    if (!id) throw new Error('Form ID is required');

    // Add aria-live region to inform screen readers that the item was added
    // Get the added text from any add-to-cart button
    const allAddToCartContainers = /** @type {NodeListOf<AddToCartComponent>} */ (
      this.querySelectorAll('add-to-cart-component')
    );
    const anyAddToCartButton = allAddToCartContainers[0]?.refs.addToCartButton;
    if (anyAddToCartButton) {
      const addedTextElement = anyAddToCartButton.querySelector('.add-to-cart-text--added');
      const addedText = addedTextElement?.textContent?.trim() || Theme.translations.added;

      this.#setLiveRegionText(addedText);

      setTimeout(() => {
        this.#clearLiveRegionText();
      }, SUCCESS_MESSAGE_DISPLAY_DURATION);
    }

    // Dispatch immediately so cart UI (e.g. mini cart drawer) can open
    // without waiting for the extra `/cart.js` request.
    this.dispatchEvent(
      new CartAddEvent({}, id.toString(), {
        source: 'product-form-component',
        itemCount,
        productId: this.dataset.productId,
        sections: response.sections,
      })
    );

    // High heat: lock UI immediately after successful add.
    if (this.dataset.isHighHeat === 'true') {
      this.#applyHighHeatButtonState({ items: [{ quantity: 1, variant_id: Number(id), product_id: Number(this.dataset.productId) }] });
    }

    // Fetch the updated cart in the background to sync variant quantity label
    // and refresh the cached cart used by the dedupe check.
    void this.#fetchAndUpdateCartQuantity();
  }

  /**
   * Updates the quantity label with the current cart quantity
   * @param {number} cartQty - The quantity in cart
   */
  #updateQuantityLabel(cartQty) {
    const quantityLabel = this.refs.quantityLabelCartCount;
    if (quantityLabel) {
      const inCartText = quantityLabel.textContent?.match(/\((\d+)\s+(.+)\)/);
      if (inCartText && inCartText[2]) {
        quantityLabel.textContent = `(${cartQty} ${inCartText[2]})`;
      }

      // Show/hide based on quantity
      quantityLabel.classList.toggle('hidden', cartQty === 0);
    }
  }

  /**
   * @param {*} text
   */
  #setLiveRegionText(text) {
    const liveRegion = this.refs.liveRegion;
    liveRegion.textContent = text;
  }

  #clearLiveRegionText() {
    const liveRegion = this.refs.liveRegion;
    liveRegion.textContent = '';
  }

  /**
   * Morphs or removes/adds an element based on current and new element states
   * @param {Element | null | undefined} currentElement - The current element in the DOM
   * @param {Element | null | undefined} newElement - The new element from the server response
   * @param {Element | null} [insertReferenceElement] - Element to insert before if adding new element
   */
  #morphOrUpdateElement(currentElement, newElement, insertReferenceElement = null) {
    if (currentElement && newElement) {
      morph(currentElement, newElement);
    } else if (currentElement && !newElement) {
      currentElement.remove();
    } else if (!currentElement && newElement && insertReferenceElement) {
      insertReferenceElement.insertAdjacentElement('beforebegin', /** @type {Element} */ (newElement.cloneNode(true)));
    }
  }

  /**
   * @param {VariantUpdateEvent} event
   */
  #onVariantUpdate = async (event) => {
    if (event.detail.data.newProduct) {
      this.dataset.productId = event.detail.data.newProduct.id;
    } else if (event.detail.data.productId !== this.dataset.productId) {
      return;
    }

    const { variantId } = this.refs;

    // Update the variant ID
    variantId.value = event.detail.resource?.id ?? '';
    
    // Update notify-me drawer variant ID if it exists
    const notifyMeVariantInput = /** @type {HTMLInputElement | null} */ (
      this.querySelector('.notify-me-drawer__variant-id')
    );
    if (notifyMeVariantInput) {
      notifyMeVariantInput.value = event.detail.resource?.id ?? '';
    }
    
    const { addToCartButtonContainer: currentAddToCartButtonContainer, acceleratedCheckoutButtonContainer } = this.refs;
    const currentAddToCartButton = currentAddToCartButtonContainer?.refs.addToCartButton;

    // Update state and text for add-to-cart button
    if (!currentAddToCartButtonContainer || (!currentAddToCartButton && !acceleratedCheckoutButtonContainer)) return;

    // Update the button state: disable only when there is no valid variant
    const hasVariant = !!event.detail.resource;
    if (!hasVariant) {
      currentAddToCartButtonContainer.disable();
    } else {
      currentAddToCartButtonContainer.enable();
    }

    // Expose current variant availability as a data attribute for CSS/other UI (e.g. notify-me drawer)
    const isOutOfStock = hasVariant && event.detail.resource.available === false;
    this.dataset.variantAvailable = !hasVariant || isOutOfStock ? 'false' : 'true';

    const newAddToCartButton = event.detail.data.html.querySelector('product-form-component [ref="addToCartButton"]');
    if (newAddToCartButton && currentAddToCartButton) {
      morph(currentAddToCartButton, newAddToCartButton);
    }

    if (acceleratedCheckoutButtonContainer) {
      if (event.detail.resource == null || event.detail.resource.available == false) {
        acceleratedCheckoutButtonContainer?.setAttribute('hidden', 'true');
      } else {
        acceleratedCheckoutButtonContainer?.removeAttribute('hidden');
      }
    }

    // High heat: variant changes must NOT re-enable add-to-cart if product already in cart.
    if (this.dataset.isHighHeat === 'true' && this.dataset.highHeatInCart === 'true') {
      void this.#fetchAndUpdateCartQuantity();
    }

    // Set the data attribute for the product variant media if it exists
    if (event.detail.resource) {
      const productVariantMedia = event.detail.resource.featured_media?.preview_image?.src;
      if (productVariantMedia) {
        this.refs.addToCartButtonContainer?.setAttribute(
          'data-product-variant-media',
          productVariantMedia + '&width=100'
        );
      }
    }

    // Check if quantity rules, price-per-item, or add-to-cart are appearing/disappearing (causes layout shift)
    const {
      quantityRules,
      pricePerItem,
      quantitySelector,
      productFormButtons,
      quantityLabel,
      quantitySelectorWrapper,
    } = this.refs;

    // Update quantity selector's min/max/step attributes and cart quantity for the new variant
    const newQuantityInput = /** @type {HTMLInputElement | null} */ (
      event.detail.data.html.querySelector('quantity-selector-component input[ref="quantityInput"]')
    );

    if (quantitySelector?.updateConstraints && newQuantityInput) {
      quantitySelector.updateConstraints(newQuantityInput.min, newQuantityInput.max || null, newQuantityInput.step);
    }

    const newQuantityRules = event.detail.data.html.querySelector('.quantity-rules');
    const isQuantityRulesChanging = !!quantityRules !== !!newQuantityRules;

    const newPricePerItem = event.detail.data.html.querySelector('price-per-item');
    const isPricePerItemChanging = !!pricePerItem !== !!newPricePerItem;

    if ((isQuantityRulesChanging || isPricePerItemChanging) && quantitySelector) {
      // Store quantity value before morphing entire container
      const currentQuantityValue = quantitySelector.getValue?.();

      const newProductFormButtons = event.detail.data.html.querySelector('.product-form-buttons');

      if (productFormButtons && newProductFormButtons) {
        morph(productFormButtons, newProductFormButtons);

        // Get the NEW quantity selector after morphing and update its constraints
        const newQuantityInputElement = /** @type {HTMLInputElement | null} */ (
          event.detail.data.html.querySelector('quantity-selector-component input[ref="quantityInput"]')
        );

        if (this.refs.quantitySelector?.updateConstraints && newQuantityInputElement && currentQuantityValue) {
          // Temporarily set the old value so updateConstraints can snap it properly
          this.refs.quantitySelector.setValue(currentQuantityValue);
          // updateConstraints will snap to valid increment if needed
          this.refs.quantitySelector.updateConstraints(
            newQuantityInputElement.min,
            newQuantityInputElement.max || null,
            newQuantityInputElement.step
          );
        }
      }
    } else {
      // Update elements individually when layout isn't changing
      /** @type {Array<[string, HTMLElement | undefined, HTMLElement | undefined]>} */
      const morphTargets = [
        ['.quantity-label', quantityLabel, quantitySelector],
        ['.quantity-rules', quantityRules, this.refs.productFormButtons],
        ['price-per-item', pricePerItem, quantitySelectorWrapper],
      ];

      for (const [selector, currentElement, fallback] of morphTargets) {
        this.#morphOrUpdateElement(currentElement, event.detail.data.html.querySelector(selector), fallback);
      }
    }

    // Morph volume pricing if it exists
    const currentVolumePricing = this.refs.volumePricing;
    const newVolumePricing = event.detail.data.html.querySelector('volume-pricing');
    this.#morphOrUpdateElement(currentVolumePricing, newVolumePricing, this.refs.productFormButtons);

    const hasB2BFeatures =
      quantityRules || newQuantityRules || pricePerItem || newPricePerItem || currentVolumePricing || newVolumePricing;

    if (!hasB2BFeatures) return;

    // Fetch and update cart quantity for the new variant
    await this.#fetchAndUpdateCartQuantity();
  };

  /**
   * Whether the form's variant id disagrees with the size checked in the main PDP
   * variant picker. Only single-option pickers are checked, because there the
   * checked radio's `data-variant-id` is exactly the variant being selected.
   * @returns {boolean}
   */
  #isVariantIdStale() {
    if (this.closest('product-card, quick-add-dialog, size-guide-modal')) return false;

    const picker = this.closest('.shopify-section')?.querySelector(
      'variant-picker[data-template-product-match="true"]:not([data-block-id*="size-guide-modal"])'
    );
    if (!picker || picker.querySelectorAll('fieldset').length !== 1) return false;

    const checked = /** @type {HTMLInputElement | null} */ (
      picker.querySelector('fieldset input[type="radio"]:checked[data-variant-id]')
    );
    const expectedId = checked?.dataset.variantId;
    if (!expectedId) return false;

    return this.refs.variantId.value !== expectedId;
  }

  /**
   * Disable the add to cart button while the UI is updating before #onVariantUpdate is called.
   * Accelerated checkout button is also disabled via its own event listener not exposed to the theme.
   */
  #onVariantSelected = () => {
    this.refs.addToCartButtonContainer?.disable();
  };
}

if (!customElements.get('product-form-component')) {
  customElements.define('product-form-component', ProductFormComponent);
}
